import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/hunting-utv-weather-curtains.jpg";

import heroPicture from "@/assets/hunting-utv-weather-curtains.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
export const Route = createFileRoute("/utv-atv-covers")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "UTV & ATV Covers and Roll-Down Weather Curtains in Texas | Texas Made Shade",
      description:
        "Custom UTV & ATV covers and roll-down weather curtains patterned to Polaris Ranger, RZR, Can-Am Defender, Maverick, Kawasaki Mule, Teryx.",
      image: hero,
      imageAlt: "Polaris Ranger UTV with custom roll-down weather curtains on a Texas hunting ranch at golden hour",
      socialTitle: "Custom UTV / ATV Weather Curtains — Patterned in Seguin, TX",
      socialDescription:
        "Roll-down weather curtains, cab covers and full enclosures for Polaris, Can-Am, Kawasaki, John Deere and Kubota side-by-sides.",
      keywords:
        "UTV weather curtains, ATV covers, side-by-side covers, Polaris Ranger covers, RZR covers, Can-Am Defender curtains, Can-Am Maverick covers, Kawasaki Mule covers, Kawasaki Teryx covers, John Deere Gator covers, Kubota RTV covers, roll-down weather curtains, UTV cab enclosure, Seguin TX",
    }),
    scripts: serviceHeadScripts({
      path: "/utv-atv-covers",
      serviceName: "UTV & ATV Covers",
      breadcrumbName: "UTV & ATV Covers",
      description: "Custom UTV and ATV covers, dog boxes, gun racks and canvas accessories built in Seguin, TX.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/utv-atv-covers"
      serviceName="UTV / ATV Weather Curtains"
      breadcrumbs={[
        { label: "Custom Hunting & Fishing Covers", to: "/hunting-fishing-covers" },
        { label: "ATV, UTV & Side-by-Side Covers with Weather Curtains" },
      ]}
      eyebrow="★ UTV / ATV WEATHER CURTAINS ★"
      title="ATV, UTV & Side-by-Side Covers with Roll-Down Weather Curtains"
      intro="Cab covers, bed covers, soft tops, full enclosures and roll-down weather curtains patterned to Polaris Ranger, RZR & General, Can-Am Defender & Maverick, Kawasaki Mule & Teryx, John Deere Gator and Kubota RTV cabs. Built to keep wind, rain and cold out of the cab on early hunts and late-season ranch rides."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Patterned to Your Cab · Seguin, TX",
        headline: "Send Make, Model & Photos for a Free Curtain Estimate.",
        subhead:
          "Tell us your machine, whether you want a partial set (front/rear) or a full enclosure with doors, and your fabric preference.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("UTV / ATV Weather Curtains")}&notes=${encodeURIComponent("UTV / ATV weather curtains — please share make, model, year and what you want covered (front, rear, doors, full enclosure).")}`,
        primaryLabel: "Get My UTV Curtain Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Roll-up & roll-down weather curtains with 30-gauge clear vinyl windows",
        "YKK zippers, twist-lock fasteners and snaps for one-handed use",
        "Heavy vinyl-coated polyester or marine canvas — UV-stable & mildew-resistant",
        "Removable in seconds with sewn-in tie-back straps",
        "Patterned to Polaris, Can-Am, Kawasaki, John Deere and Kubota cabs",
        "Cab covers, bed covers and soft-top options",
      ]}
      body={[
        {
          heading: "Roll-Down Weather Curtains for Hunting & Ranch Rigs",
          text: "Block wind, rain, cold and dust without losing visibility. Curtains attach to your existing cab frame with snaps, twist-locks and zip-on tracks, and roll up with sewn-in tie-back straps for warm weather.",
          bullets: [
            "30-gauge clear vinyl windows",
            "Snap and twist-lock fastening for one-handed use",
            "Sewn-in roll-up straps",
          ],
          cta: { label: "See Roll-Down Curtains & Drop Panels", href: "/curtains" },
        },
        {
          heading: "Full Cab Enclosures with Doors",
          text: "Step up to a full enclosure with patterned doors, front and rear panels and reinforced corners — built to handle long days on the lease without tearing or leaking.",
          bullets: [
            "Patterned doors with marine zippers",
            "Front, side and rear panels",
            "Reinforced corners and stress points",
          ],
        },
        {
          heading: "Fits Polaris, Can-Am, Kawasaki, John Deere & Kubota",
          text: "We pattern to Polaris Ranger, RZR & General, Can-Am Defender & Maverick, Kawasaki Mule & Teryx, John Deere Gator and Kubota RTV cabs. Bring it to our Seguin shop or send detailed photos and measurements for an off-site build.",
          cta: { label: "Send Make, Model and Photos", href: "/contact" },
        },
      ]}
      applicationsEyebrow="MACHINES WE FIT"
      applicationsHeading="Machines We Fit"
      applications={[
        "Polaris Ranger",
        "Polaris RZR",
        "Polaris General",
        "Can-Am Defender",
        "Can-Am Maverick",
        "Kawasaki Mule",
        "Kawasaki Teryx",
        "John Deere Gator",
        "Kubota RTV",
      ]}
      faqs={[
        {
          q: "Do weather curtains fit my Polaris, Can-Am, Kawasaki, John Deere or Kubota?",
          a: "Yes — we pattern roll-down weather curtains to your specific UTV or side-by-side cab. Bring it to our Seguin shop or send detailed photos and measurements for an off-site build.",
        },
        {
          q: "What materials do you use for UTV weather curtains?",
          a: "Sunbrella® Marine, Top Gun®, Aqualon Edge, heavy vinyl-coated polyester and marine vinyl, paired with 30-gauge clear vinyl windows, YKK marine zippers, twist-lock fasteners and snaps. All UV-stable, water-repellent and mildew-resistant.",
        },
        {
          q: "How are roll-down curtains installed?",
          a: "Curtains attach to your existing cab frame with snaps, twist-locks and zip-on tracks. They roll up and tie back with sewn-in straps for warm weather, and roll down in seconds when wind, rain or cold rolls in.",
        },
        {
          q: "How much do custom UTV weather curtains cost?",
          a: "Pricing depends on your machine, fabric choice and whether you want a partial set (front/rear) or a full enclosure with doors. Most weather curtain sets fall between a few hundred and just over a thousand dollars.",
        },
      ]}
    >
      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
          <Link
            to="/hunting-fishing-covers"
            className="inline-flex items-center border-2 border-navy-deep px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep hover:bg-navy-deep hover:text-white"
          >
            ← Back to All Hunting Covers
          </Link>
        </div>
      </section>
    </ServicePage>
  ),
});
